import type {
  LiveSessionRequest,
  PlaybackSessionRequest,
  VgpsLiveSession,
  VgpsLivekitWebRuntime,
  VgpsPlaybackSession,
} from 'livekit_web';

type AnySession = VgpsLiveSession | VgpsPlaybackSession;

interface RegistryEntry {
  session: AnySession;
  refCount: number;
}

const registry = new Map<string, RegistryEntry>();

async function leaseSession<T extends AnySession>(
  key: string,
  factory: () => Promise<T>,
): Promise<{ session: T; release: () => Promise<void> }> {
  let entry = registry.get(key);

  if (!entry) {
    const session = await factory();
    entry = { session, refCount: 0 };
    registry.set(key, entry);
  }

  entry.refCount += 1;

  return {
    session: entry.session as T,
    release: async (): Promise<void> => {
      const e = registry.get(key);
      if (!e) return;
      e.refCount -= 1;
      if (e.refCount <= 0) {
        registry.delete(key);
        await e.session.close();
      }
    },
  };
}

export async function leaseLiveSession(
  runtime: VgpsLivekitWebRuntime,
  req: { deviceSerial: string; channelId: string; qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto' },
): Promise<{ session: VgpsLiveSession; release: () => Promise<void> }> {
  const key = `live:${req.deviceSerial}:${req.channelId}:${req.qualityHint ?? 'auto'}`;
  const request: LiveSessionRequest = {
    deviceSerial: req.deviceSerial,
    channelId: req.channelId,
    playOptions: { qualityHint: req.qualityHint },
  };
  return leaseSession(key, () => runtime.openLiveSession(request));
}

export async function leasePlaybackSession(
  runtime: VgpsLivekitWebRuntime,
  req: {
    deviceSerial: string;
    channelId: string;
    startTsMs: number;
    endTsMs: number;
    qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';
  },
): Promise<{ session: VgpsPlaybackSession; release: () => Promise<void> }> {
  const key = `playback:${req.deviceSerial}:${req.channelId}:${req.startTsMs}:${req.endTsMs}:${req.qualityHint ?? 'auto'}`;
  const request: PlaybackSessionRequest = {
    deviceSerial: req.deviceSerial,
    channelId: req.channelId,
    playOptions: { qualityHint: req.qualityHint },
    timeRange: { startTsMs: req.startTsMs, endTsMs: req.endTsMs },
  };
  return leaseSession(key, () => runtime.openPlaybackSession(request));
}

/** Clear all registry entries — call after runtime.dispose(). */
export function clearRegistry(): void {
  registry.clear();
}
